import * as React from "react";
import Modal from "../Modal";
import { manageData } from "../../requests/CRUD";
import { VENDOR_NOTES } from "../../consts/tables";
import { formatDateUSA, speechErrorMessage } from "../../utils";
import ConfirmationDialog from "../ConfirmDialog";

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

/**
 * A vendor's notes, one row per note — the same timeline the property screen
 * shows, reusing its styles so the two read alike.
 */
const VendorNotesModal = ({
    notes,
    show,
    onClose,
    vendorId,
    currentUser,
    fetchNotes,
    onAddPendingNote,
    onEditPendingNote,
    onDeletePendingNote,
}) => {
    const [saving, setSaving] = React.useState(false);
    const [newNoteText, setNewNoteText] = React.useState("");
    const [editingNoteId, setEditingNoteId] = React.useState(null);
    const [editingText, setEditingText] = React.useState("");
    const [deletingNoteId, setDeletingNoteId] = React.useState(null);
    const [isListening, setIsListening] = React.useState(false);
    const recognitionRef = React.useRef(null);

    const startListening = (targetSetter) => {
        if (!SpeechRecognition) {
            alert("Speech recognition is not supported in this browser.");
            return;
        }
        const recognition = new SpeechRecognition();
        recognition.lang = "en-US";
        recognition.interimResults = false;
        recognition.continuous = true;
        recognitionRef.current = recognition;

        recognition.onresult = (event) => {
            let transcript = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    transcript += event.results[i][0].transcript;
                }
            }
            if (transcript) {
                targetSetter((prev) => prev ? prev + " " + transcript : transcript);
            }
        };
        // Without this the failure is invisible: the button flicks on and off and
        // nothing says why. "not-allowed"/"service-not-allowed" is the mic being
        // blocked for the site; "network" is Chrome's speech service unreachable.
        recognition.onerror = (event) => {
            setIsListening(false);
            recognitionRef.current = null;

            const reason = event && event.error;

            if (reason === 'no-speech' || reason === 'aborted') return;

            alert(speechErrorMessage(reason));
        };
        recognition.onend = () => {
            setIsListening(false);
            recognitionRef.current = null;
        };

        try {
            recognition.start();
            setIsListening(true);
        } catch (e) {
            // start() throws if a previous recognition is still running.
            recognitionRef.current = null;
            setIsListening(false);
        }
    };

    const stopListening = () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }
        setIsListening(false);
    };

    React.useEffect(() => {
        if (show) {
            setNewNoteText("");
            setEditingNoteId(null);
            setEditingText("");
            setDeletingNoteId(null);
        }
        if (!show) {
            stopListening();
        }
    }, [show, vendorId]);

    const noteKey = (note) => note.ARCIMEDES_ROW_ID || note.PENDING_ID;

    const isPendingId = (id) => typeof id === "string" && id.startsWith("pending-");

    const handleStartEdit = (note) => {
        setEditingNoteId(noteKey(note));
        setEditingText(note.NOTE_TEXT || "");
    };

    const handleCancelEdit = () => {
        setEditingNoteId(null);
        setEditingText("");
    };

    const handleSaveEdit = () => {
        const text = editingText.trim();
        if (!text || !editingNoteId || saving) return;
        if (isPendingId(editingNoteId)) {
            onEditPendingNote(editingNoteId, text);
            setEditingNoteId(null);
            setEditingText("");
            return;
        }
        const now = new Date().toISOString();
        const userName = currentUser || "User";
        setSaving(true);
        manageData({
            tableCode: VENDOR_NOTES,
            data: [{
                ARCIMEDES_ROW_ID: editingNoteId,
                NOTE_TEXT: text,
                LAST_MODIFIED_DATE: now,
                LAST_MODIFIED_BY: userName,
            }],
        })
        .then((res) => {
            if (res && res.success) {
                setEditingNoteId(null);
                setEditingText("");
                fetchNotes();
            }
        })
        .finally(() => setSaving(false));
    };

    const handleDeleteNote = (value) => {
        if (value !== 'ok' || !deletingNoteId || saving) {
            setDeletingNoteId(null);
            return;
        }
        if (isPendingId(deletingNoteId)) {
            onDeletePendingNote(deletingNoteId);
            setDeletingNoteId(null);
            return;
        }
        setSaving(true);
        manageData({
            tableCode: VENDOR_NOTES,
            data: [{
                ARCIMEDES_ROW_ID: deletingNoteId,
                DELETED: 1,
            }],
        })
        .then((res) => {
            if (res && res.success) {
                setDeletingNoteId(null);
                fetchNotes();
            }
        })
        .finally(() => setSaving(false));
    };

    const handleAddNote = () => {
        const text = newNoteText.trim();
        if (!text || saving) return;
        // Vendor not created yet: hand the note up to be held in form state and
        // written once the vendor has an id.
        if (!vendorId) {
            onAddPendingNote(text);
            setNewNoteText("");
            return;
        }
        const now = new Date().toISOString();
        const userName = currentUser || "User";
        setSaving(true);
        manageData({
            tableCode: VENDOR_NOTES,
            data: [{
                VENDOR_ID: Number(vendorId),
                NOTE_TEXT: text,
                DATE_CREATED: now,
                CREATED_BY: userName,
                LAST_MODIFIED_DATE: now,
                LAST_MODIFIED_BY: userName,
                DELETED: 0,
            }],
        })
        .then((res) => {
            if (res && res.success) {
                setNewNoteText("");
                fetchNotes();
            }
        })
        .finally(() => setSaving(false));
    };

    const isUpdated = (note) => {
        if (!note.LAST_MODIFIED_DATE || !note.DATE_CREATED) return false;
        const created = new Date(note.DATE_CREATED).getTime();
        const modified = new Date(note.LAST_MODIFIED_DATE).getTime();
        return modified - created > 2000;
    };

    const noteDateLine = (note) => {
        const updated = isUpdated(note);
        const when = updated
            ? formatDateUSA(note.LAST_MODIFIED_DATE, true)
            : formatDateUSA(note.DATE_CREATED, true);

        return when ? when.replace(" ", ", ") : "";
    };

    /**
     * Notes carried over from the old single NOTES box have no author, so they
     * get no heading rather than one naming nobody.
     */
    const noteMetaLine = (note) => {
        const updated = isUpdated(note);
        const name = (updated
            ? (note.LAST_MODIFIED_BY || note.CREATED_BY)
            : note.CREATED_BY) || "";

        if (!name.trim()) return "";

        return `${updated ? "Updated" : "Created"} Note by ${name}`;
    };

    if (!show) return null;

    return (
        <React.Fragment>
        <Modal show={show} onClose={onClose} className="property-notes-modal-wrapper">
            <div className="Property-notes-modal">
                <div className="Property-notes-modal-header">
                    <h3 className="Property-notes-modal-title">Vendor Notes</h3>
                    <button
                        type="button"
                        className="Property-notes-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
                <div className="Property-notes-modal-body">
                    <div className="Property-notes-modal-list">
                        {notes.length === 0 ? (
                            <div className="Property-notes-modal-empty">No notes yet. Add one below.</div>
                        ) : (
                            <div>
                                <div className="Property-notes-modal-timeline-line" aria-hidden="true" />
                                {notes.map((note) => (
                                    <div key={noteKey(note) || note.NOTE_TEXT} className="Property-notes-modal-note">
                                        <div className="Property-notes-modal-note-date">{noteDateLine(note)}</div>
                                        <div className="Property-notes-modal-note-line">
                                            <span className="Property-notes-modal-note-dot" />
                                        </div>
                                        <div className="Property-notes-modal-note-content">
                                            <div className="Property-notes-modal-note-header">
                                                {noteMetaLine(note) && <span>{noteMetaLine(note)}</span>}
                                                {editingNoteId !== noteKey(note) && (
                                                    <div className="Property-notes-modal-note-actions">
                                                        <button
                                                            type="button"
                                                            className="Property-notes-modal-edit-btn"
                                                            onClick={() => handleStartEdit(note)}
                                                            title="Edit note"
                                                        >
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="Property-notes-modal-delete-btn"
                                                            onClick={() => setDeletingNoteId(noteKey(note))}
                                                            title="Delete note"
                                                        >
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="3 6 5 6 21 6" />
                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {editingNoteId === noteKey(note) ? (
                                                <div className="Property-notes-modal-edit-area">
                                                    <textarea
                                                        className="Property-notes-modal-edit-textarea"
                                                        value={editingText}
                                                        onChange={(e) => setEditingText(e.target.value)}
                                                        disabled={saving}
                                                        autoFocus
                                                    />
                                                    <div className="Property-notes-modal-edit-actions">
                                                        <button
                                                            type="button"
                                                            className="Property-notes-modal-edit-save"
                                                            onClick={handleSaveEdit}
                                                            disabled={!editingText.trim() || saving}
                                                        >
                                                            {saving ? "Saving..." : "Save"}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="Property-notes-modal-edit-cancel"
                                                            onClick={handleCancelEdit}
                                                            disabled={saving}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="Property-notes-modal-note-text">{note.NOTE_TEXT}</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="Property-notes-modal-add">
                        <textarea
                            className="Property-notes-modal-textarea"
                            placeholder="Add a note..."
                            value={newNoteText}
                            onChange={(e) => setNewNoteText(e.target.value)}
                            disabled={saving}
                        />
                        <div className="Property-notes-modal-add-actions">
                            <button
                                type="button"
                                className="Property-notes-modal-add-btn"
                                onClick={handleAddNote}
                                disabled={!newNoteText.trim() || saving}
                            >
                                {saving ? "Saving..." : "Add note"}
                            </button>
                            <button
                                type="button"
                                className={`Property-notes-modal-mic-btn${isListening ? " listening" : ""}`}
                                onClick={() => isListening ? stopListening() : startListening(setNewNoteText)}
                                title={isListening ? "Stop recording" : "Start voice input"}
                                disabled={saving}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                    <line x1="12" y1="19" x2="12" y2="23" />
                                    <line x1="8" y1="23" x2="16" y2="23" />
                                </svg>
                                {isListening && <span className="Property-notes-modal-mic-pulse" />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
        <ConfirmationDialog
            open={!!deletingNoteId}
            onClose={handleDeleteNote}
            content="Are you sure you want to delete this note?"
            style={{ zIndex: 10002 }}
        />
    </React.Fragment>
    );
};

export default VendorNotesModal;
